import type { ProductApiItem } from '@/app/lib/api/types-api'

import { isLinkableProduct, mapProductToCard } from './mapProductToCard'

/**
 * Tarjeta de listado de producto: lo que devuelve `mapProductToCard` —con su
 * `priceDisplay` garantizado, de ahí el `ReturnType` en vez de ensanchar a
 * `VerticalCardData`— más el `id` del producto. La card no usa el `id`, pero el
 * consumidor lo necesita como `key` de React estable: dos productos pueden
 * compartir `purchase_url`, así que el `href` no garantiza unicidad.
 */
export type ProductCard = ReturnType<typeof mapProductToCard> & {
  readonly id: string
}

/**
 * `ProductApiItem[]` → tarjetas listas para `VerticalCard`, preservando el orden
 * en que el backend las devuelve.
 *
 * `options` se reenvía tal cual a `mapProductToCard` (p. ej.
 * `preferVerticalImage` para tarjetas de póster como `RankingCard`).
 *
 * Compartida por los módulos CMS de producto que muestran botón de compra
 * (`ProductosDestacados`, `ProductosBotonCompra`): ambos necesitan exactamente el
 * mismo criterio de "producto utilizable" y el mismo mapeo, así que vive junto a
 * `mapProductToCard`, de la que ya depende, en vez de duplicarse en cada módulo
 * (specs/030-productos-boton-compra-module/research.md §3). Ojo: `OtrosProductosIzq`
 * NO la usa — su tarjeta no tiene CTA y su `href` nunca cae a `purchase_url`, así
 * que mantiene su propio `toRailCards`.
 *
 * Descarta un producto solo en dos casos, para no pintar una tarjeta inútil:
 * - sin `title` (no habría nada que enlazar ni leer);
 * - sin destino coherente (`isLinkableProduct`): llega sin `genres`, así que no
 *   hay URL de ficha (PDP) derivable — la tarjeta nunca cae a `purchase_url`
 *   como destino propio, aunque el producto sí sea comprable.
 *
 * El resto se pinta con el dato que falte omitido: `VerticalCard` ya es defensiva
 * con imagen, fecha, ciudad y precio ausentes
 * (`specs/023-productos-destacados-module`, research §7).
 */
export function toProductCards(
  products: ProductApiItem[],
  options?: Parameters<typeof mapProductToCard>[1],
): ProductCard[] {
  return products
    .filter((product) => Boolean(product.title?.trim()) && isLinkableProduct(product))
    .map((product) => ({ ...mapProductToCard(product, options), id: product.id }))
}
